import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, orderBy, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { Spin } from "antd";
import { isSlotAvailable } from "../lib/loans";

// Collections qui ont une page codée à la main. Les autres utilisent GenericEquipmentPage.
const DEDICATED_PAGES = ["calculator_slots", "powerbank_slots", "headphone_slots"];

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    cardBg: "#ffffff", green: "#16a34a", greenBg: "#f0fdf4",
    modalBg: "rgba(15,23,42,0.55)",
    modalCard: "#ffffff",
    modalBorder: "#e2e8f0",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    cardBg: "#1e293b", green: "#4ade80", greenBg: "#052e16",
    modalBg: "rgba(0,0,0,0.72)",
    modalCard: "#1e293b",
    modalBorder: "#273549",
    red: "#f87171", redBg: "#450a0a", redBorder: "#7f1d1d",
  },
};

// ── Damage disclaimer modal ───────────────────────────────────────────────────
function DamageModal({ t, dark, onClose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: t.modalBg,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      animation: "fadeIn 0.2s ease",
    }}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(24px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>
      <div style={{
        background: t.modalCard,
        border: `1.5px solid ${t.modalBorder}`,
        borderRadius: 20,
        padding: "28px 24px",
        maxWidth: 400,
        width: "100%",
        boxShadow: dark
          ? "0 24px 60px rgba(0,0,0,0.6)"
          : "0 24px 60px rgba(15,23,42,0.18)",
        animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: t.redBg,
            border: `1.5px solid ${t.redBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, flexShrink: 0,
          }}>
            ⚠️
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: t.text1, lineHeight: 1.2 }}>
              Responsabilité d'emprunt
            </div>
            <div style={{ fontSize: 12, color: t.text2, marginTop: 2 }}>
              À lire avant d'emprunter
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: t.modalBorder, marginBottom: 16 }} />

        {/* Body */}
        <div style={{
          background: t.redBg,
          border: `1px solid ${t.redBorder}`,
          borderRadius: 12,
          padding: "14px",
          marginBottom: 16,
        }}>
          <p style={{ fontSize: 13, color: t.red, fontWeight: 700, margin: "0 0 6px" }}>
            🚨 En cas de dommage, casse ou perte :
          </p>
          <p style={{ fontSize: 13, color: dark ? "#fca5a5" : "#991b1b", margin: 0, lineHeight: 1.65 }}>
            Tout équipement emprunté qui revient <strong>cassé, endommagé, ou perdu</strong> engage la 
            responsabilité totale de l'emprunteur. Le <strong>prix intégral de remplacement</strong> de 
            l'équipement sera facturé.
          </p>
        </div>

        <div style={{
          background: t.bg3,
          border: `1px solid ${t.border}`,
          borderRadius: 12,
          padding: "12px 14px",
          marginBottom: 20,
        }}>
          <p style={{ fontSize: 12, color: t.text2, margin: 0, lineHeight: 1.6 }}>
            💡 Manipulez le matériel avec soin. En cas de problème préexistant, 
            signalez-le <strong style={{ color: t.text1 }}>avant</strong> de quitter l'accueil.
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "13px",
            borderRadius: 12,
            border: "none",
            background: dark ? "#2563eb" : "#0f172a",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            letterSpacing: "0.01em",
          }}
        >
          J'ai compris — Continuer
        </button>
      </div>
    </div>
  );
}

// ── Live availability hook ────────────────────────────────────────────────────
function useAvailability(collectionName) {
  const [available, setAvailable] = useState(null);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    if (!collectionName) return;
    const unsub = onSnapshot(collection(db, collectionName), snap => {
      const slots = snap.docs.map(d => d.data());
      setTotal(slots.length);
      setAvailable(slots.filter(isSlotAvailable).length);
    });
    return () => unsub();
  }, [collectionName]);

  return { available, total };
}

// ── Grid card ─────────────────────────────────────────────────────────────────
function CategoryCard({ cat, dark, t }) {
  const navigate = useNavigate();
  const { available, total } = useAvailability(cat.collection || null);
  const comingSoon = !cat.collection || cat.active === false;
  const color = dark ? (cat.darkColor || cat.color) : cat.color;
  const bg = dark ? (cat.darkBg || cat.bg) : cat.bg;

  const availColor = available === 0 ? "#ef4444"
    : available <= 2 ? "#f59e0b"
    : "#22c55e";

  return (
    <div
      onClick={() => !comingSoon && navigate(
        DEDICATED_PAGES.includes(cat.collection) && cat.route
          ? cat.route                    // page dédiée (calculatrices, powerbanks, casques)
          : `/equipment/${cat.id}`       // toute catégorie créée depuis l'admin
      )}
      style={{
        background: t.cardBg,
        border: `1.5px solid ${t.border}`,
        borderRadius: 16,
        padding: "14px 12px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
        cursor: comingSoon ? "default" : "pointer",
        opacity: comingSoon ? 0.5 : 1,
        transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
        position: "relative",
        textAlign: "center",
        minHeight: 160,
      }}
      onMouseEnter={e => {
        if (!comingSoon) {
          e.currentTarget.style.borderColor = color;
          e.currentTarget.style.boxShadow = `0 4px 16px ${color}33`;
          e.currentTarget.style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = t.border;
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      {/* Icon */}
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: bg, display: "flex",
        alignItems: "center", justifyContent: "center",
        fontSize: 28, flexShrink: 0,
      }}>
        {cat.emoji}
      </div>

      {/* Title */}
      <div style={{ fontSize: 14, fontWeight: 700, color: t.text1, lineHeight: 1.2 }}>
        {cat.title}
      </div>

      {/* Availability */}
      {!comingSoon && available !== null && total !== null && (
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{
            width: 6, height: 6, borderRadius: "50%",
            background: availColor,
            boxShadow: `0 0 5px ${availColor}`,
            flexShrink: 0,
          }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: availColor }}>
            {available === 0
              ? "Aucun dispo"
              : `${available}/${total} dispo`}
          </span>
        </div>
      )}

      {/* Loading */}
      {!comingSoon && available === null && cat.collection && (
        <div style={{ fontSize: 10, color: t.text3 }}>Chargement…</div>
      )}

      {/* Coming soon */}
      {comingSoon && (
        <div style={{
          fontSize: 9, padding: "2px 8px", borderRadius: 20,
          background: t.bg3, color: t.text3, fontWeight: 500,
        }}>
          Bientôt disponible
        </div>
      )}

      {/* Arrow bottom-right */}
      {!comingSoon && (
        <div style={{
          position: "absolute", bottom: 10, right: 12,
          fontSize: 14, color: t.text3,
        }}>→</div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function EquipmentPage() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(true);

  const t = dark ? theme.dark : theme.light;

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "equipment_categories"), orderBy("order", "asc"))
        );
        setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchCategories();
  }, []);

  return (
    <div style={{
      background: t.bg, minHeight: "100vh",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
      `}</style>

      {/* Damage Modal */}
      {showModal && (
        <DamageModal t={t} dark={dark} onClose={() => setShowModal(false)} />
      )}

      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", padding: "16px 18px 0",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy"
              style={{ height: 36, objectFit: "contain" }}
              onError={e => e.target.style.display = "none"}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Équipements</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Matériel disponible à l'emprunt</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{
              width: 36, height: 20, borderRadius: 20, cursor: "pointer",
              position: "relative", background: t.bg3,
              border: `1px solid ${t.border}`, flexShrink: 0,
            }}>
              <div style={{
                width: 14, height: 14, borderRadius: "50%",
                background: t.blue, position: "absolute",
                top: 2, left: 2, transition: "transform 0.2s",
                transform: dark ? "translateX(16px)" : "translateX(0)",
              }} />
            </button>
            <button onClick={() => navigate("/")} style={{
              fontSize: 12, borderRadius: 8, padding: "6px 10px",
              cursor: "pointer", background: t.bg3,
              border: `1px solid ${t.border}`, color: t.text2,
            }}>
              ← Retour
            </button>
          </div>
        </div>

        {/* Info banner */}
        <div style={{
          margin: "16px 18px 0", background: t.blueBg,
          border: `1px solid ${t.border}`, borderRadius: 14,
          padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start",
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>ℹ️</span>
          <div style={{ fontSize: 12, color: t.blueText, lineHeight: 1.6 }}>
            Sélectionnez un équipement pour voir la disponibilité en temps réel et l'emprunter. Servez-vous, puis déclarez le retour : <strong>l'accueil WeStudy</strong> le valide.
          </div>
        </div>

        {/* Grid */}
        <div style={{ padding: "20px 18px 0" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spin />
            </div>
          ) : categories.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: t.text3, fontSize: 13 }}>
              Aucun équipement disponible pour le moment.
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}>
              {categories.map(cat => (
                <CategoryCard key={cat.id} cat={cat} dark={dark} t={t} />
              ))}
            </div>
          )}
        </div>

      

      </div>
    </div>
  );
}